import React, { useContext, useEffect, useState } from "react";
import OwButton from "../../../../shared/OwButton/OwButton";
import IPrice_BandUI from "../../../../../models/IPrice_BandUI";
import IPrice_Group from "../../../../../models/IPrice_Group";
import IVendor_Source from "../../../../../models/IVendor_Source";
import IPrice_Band from "../../../../../models/IPrice_Band";
import IPrice_Band_Compiled from "../../../../../models/IPrice_Band_Compiled";
import ICatalog_Item from "../../../../../models/ICatalog_Item";
import ICatalog from "../../../../../models/ICatalog";
import SupplierContext from "../../../../../contexts/SupplierContext";
import { v4 } from "uuid";
import UIService from "../../../../../services/UIService";
import OwLabel from "../../../../shared/OwLabel/OwLabel";
import OwInputWrapper from "../../../../shared/OwInputWrapper/OwInputWrapper";
import { PriceBandBuilderTableRow } from "./PriceBandBuilderTableRow/PriceBandBuilderTableRow";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

type Props = {
    catalogId: number;
    catalogItem?: ICatalog_Item;
    band: IPrice_Band_Compiled[];
    priceGroup?: IPrice_Group;
    vendorSource?: IVendor_Source;
    isCost?: boolean;
    handleBandUpdate: (band: IPrice_Band_Compiled[], priceGroup?: IPrice_Group, vendorSource?: IVendor_Source) => void;
};

export const CatalogItemPriceBandBuilder = (props: Props) => {
    const supplierModel = useContext(SupplierContext);

    const processBands = () => {
        const _band = props.band.map((band) => {
            return {
                ...band,
                deleteFlag: false,
            } as IPrice_BandUI;
        });

        return _band;
    };

    const [bands, setBands] = useState<Partial<IPrice_BandUI>[]>(processBands());
    const [isCost, setIsCost] = useState<boolean>(!!props.isCost);
    const [isDirty, setIsDirty] = useState<boolean>(false);

    useEffect(() => {
        if (isDirty) {
            props.handleBandUpdate(bands as IPrice_Band_Compiled[], props.priceGroup, props.vendorSource);
            setIsDirty(false);
        }
    }, [bands]);

    useEffect(() => {
        setBands(processBands());
    }, [props.band]);

    const addPriceBand = () => {
        const newBand = {
            uuid: v4(),
            price: 0,
            start_qty: 0,
            end_qty: 0,
            is_cost_only: !!props.isCost,
            vendor_source_id: props.vendorSource?.id,
            price_group_id: props.priceGroup?.id,
            catalog_item_id: props.catalogItem?.id,
            catalog_id: props.catalogId,
            supplier_id: supplierModel.id,
            deleteFlag: false,
        } as IPrice_BandUI;

        const newBands = [...bands, newBand];
        setBands(newBands);
        setIsDirty(true);
    };

    const removePriceBand = (index: number) => {
        const newBands = [...bands];

        // if an existing band, mark it for deletion, otherwise remove it
        if (newBands[index].guid) {
            newBands[index].deleteFlag = true;
        } else {
            newBands.splice(index, 1);
        }
        setBands(newBands);
        setIsDirty(true);
    };

    const updateBand = (band: Partial<IPrice_BandUI>, index: number) => {
        const newBands = [...bands];
        newBands[index] = band;
        setBands(newBands);
        setIsDirty(true);
    };

    const checkBandOverlap = (currentBand: Partial<IPrice_BandUI>, allBands: Partial<IPrice_BandUI>[]) => {
        const currentStart = Number(currentBand.start_qty) || 0;
        const currentEnd = Number(currentBand.end_qty) || 0;

        // Check against all other bands
        const overlap = allBands.some((band) => {
            // Skip comparing with itself
            if (band === currentBand) return false;

            const bandStart = Number(band.start_qty) || 0;
            const bandEnd = Number(band.end_qty) || 0;

            // Check if bands overlap
            // Two bands overlap if one band's start is within the other band's range
            // or if one band's end is within the other band's range
            return (
                (currentStart >= bandStart && currentStart <= bandEnd) || // Current band's start is within another band
                (currentEnd >= bandStart && currentEnd <= bandEnd) || // Current band's end is within another band
                (bandStart >= currentStart && bandStart <= currentEnd) // Another band's start is within current band
            );
        });

        if (overlap) {
            return (
                <tr>
                    <td></td>
                    <td colSpan={3} className={"text-danger"}>
                        <OwLabel title={"overlapping_bands"} />
                    </td>
                </tr>
            );
        }
        return null;
    };

    const validateBandRange = (band: Partial<IPrice_BandUI>) => {
        const startQty = Number(band.start_qty || 0);
        const endQty = band.end_qty ? Number(band.end_qty) : undefined;

        if (endQty && startQty > endQty) {
            return (
                <tr>
                    <td></td>
                    <td colSpan={3} className={"text-danger"}>
                        <OwLabel title={"start_qty_greater_than_end_qty"} />
                    </td>
                </tr>
            );
        }
        return null;
    };

    const validateBand = (band: Partial<IPrice_BandUI>) => {
        // First check the range
        const rangeError = validateBandRange(band);
        if (rangeError) return rangeError;

        // Then check for overlaps
        return checkBandOverlap(band, bands);
    };

    return (
        <div className={"CatalogItemPriceBandBuilder"}>
            <OwButton title={"price_band"} leftIconClass={"fas fa-plus"} fn={addPriceBand} />
            {bands.length ? (
                <table className={"mt-3 table table-striped " + (UIService.isMainViewLight() ? "" : "table-dark")}>
                    <thead>
                        <tr>
                            <th></th>
                            <th>
                                <OwLabel title={"start_qty"} />
                            </th>
                            <th>
                                <OwLabel title={"end_qty"} />
                                <OverlayTrigger
                                    overlay={
                                        <Tooltip>
                                            <OwLabel title={"leave_blank_if_no_end_qty"} />
                                        </Tooltip>
                                    }>
                                    <i className={"ms-2 fas fa-info-circle text-info"}></i>
                                </OverlayTrigger>
                            </th>
                            <th>
                                <OwLabel title={"price"} />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {bands.map((band, index) => {
                            return (
                                <>
                                    <PriceBandBuilderTableRow
                                        key={band.uuid || band.guid}
                                        band={band}
                                        removePriceBand={removePriceBand}
                                        index={index}
                                        updateBand={updateBand}
                                    />
                                    {validateBand(band)}
                                </>
                            );
                        })}
                    </tbody>
                </table>
            ) : (
                <div>
                    <OwLabel title={"no_price_bands"} />
                </div>
            )}
        </div>
    );
};
